// src/hooks/useMlOps.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — live data hooks for the AI Intelligence Center.
//
// useMlOverview()  → { data, loading, error, refresh, lastUpdated }
//   • first load + refresh on every websocket 'ml_events' message (debounced 1.2 s)
//   • polling fallback: every 8 s while a training run is active, every 60 s otherwise
//   • pauses while the tab is hidden, refreshes when it becomes visible again
//   • never overlaps requests; ignores results after unmount
//
// useMlEvents(handler) → subscribes to 'ml_events' (normalised payload) for the component's life
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import mlOpsService from '../services/mlOpsService';
import webSocketService from '../services/websocketService';
import { mlEventOf } from '../components/ml/mlFormat';

export function useMlEvents(handler) {
  const ref = useRef(handler);
  useEffect(() => { ref.current = handler; }, [handler]);
  useEffect(() => {
    const onMsg = (msg) => { const ev = mlEventOf(msg); if (ev) ref.current?.(ev); };
    try {
      webSocketService?.subscribe?.('ml_events', onMsg);
      if (webSocketService && webSocketService.isConnected === false) webSocketService.connect?.();
    } catch (_) { /* websocket optional — polling covers it */ }
    return () => { try { webSocketService?.unsubscribe?.('ml_events', onMsg); } catch (_) { /* ignore */ } };
  }, []);
}

export function useMlOverview({ enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const mounted = useRef(true);
  const inflight = useRef(null);
  const debounce = useRef(null);
  const dataRef = useRef(null);

  const load = useCallback(async ({ fresh = false } = {}) => {
    if (!enabled) return null;
    if (inflight.current) return inflight.current;
    const p = (async () => {
      try {
        const d = await mlOpsService.getOverview({ fresh });
        if (!mounted.current) return null;
        dataRef.current = d;
        setData(d); setError(null); setLastUpdated(new Date());
        return d;
      } catch (e) {
        if (mounted.current) setError(e);
        return null;
      } finally {
        if (mounted.current) setLoading(false);
        inflight.current = null;
      }
    })();
    inflight.current = p;
    return p;
  }, [enabled]);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; if (debounce.current) clearTimeout(debounce.current); };
  }, [load]);

  // polling fallback, faster while a run is active
  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const schedule = () => {
      const active = (dataRef.current?.summary?.activeJobs || 0) > 0;
      timer = setTimeout(async () => {
        if (typeof document === 'undefined' || !document.hidden) await load();
        schedule();
      }, active ? 8000 : 60000);
    };
    schedule();
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearTimeout(timer); document.removeEventListener('visibilitychange', onVis); };
  }, [enabled, load]);

  useMlEvents(useCallback(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(), 1200);
  }, [load]));

  const refresh = useCallback(() => load({ fresh: true }), [load]);
  return { data, loading, error, refresh, lastUpdated };
}
